import { useMemo, useEffect, useState } from 'react';

type Resource = {
  id: string;
  name: string;
  type: string;
};


export function useResourceSearch(
  resources: Resource[],
  typeSearch: string,
  nameSearch: string
) {
  const [debouncedTypeSearch, setDebouncedTypeSearch] = useState(typeSearch ?? '');
  const [debouncedNameSearch, setDebouncedNameSearch] = useState(nameSearch ?? '');

  useEffect(() => {
    // If cleared, update instantly
    if (typeSearch === '') {
      setDebouncedTypeSearch('');
    } else {
      const t = setTimeout(() => setDebouncedTypeSearch(typeSearch), 300);
      return () => clearTimeout(t);
    }
  }, [typeSearch]);

  useEffect(() => {
    if (nameSearch === '') {
      setDebouncedNameSearch('');
    } else {
      const t = setTimeout(() => setDebouncedNameSearch(nameSearch), 300);
      return () => clearTimeout(t);
    }
  }, [nameSearch]);

  // Precompute lowercase fields
  const resourcesWithLowercase = useMemo(() => {
    return resources.map(r => ({
      ...r,
      nameLower: r.name.toLowerCase(),
      typeLower: r.type.toLowerCase(),
    }));
  }, [resources]);

  // Group by type
  const resourcesByType = useMemo(() => {
    const map = new Map<string, typeof resourcesWithLowercase>();
    resourcesWithLowercase.forEach(resource => {
      const arr = map.get(resource.type) || [];
      map.set(resource.type, [...arr, resource]);
    });
    return map;
  }, [resourcesWithLowercase]);


  // Filter by type and name
  const filteredResourcesByType = useMemo(() => {
    let filtered = resourcesByType;

    if (debouncedTypeSearch.trim()) {
      const typeSearchLower = debouncedTypeSearch.trim().toLowerCase();
      const typeFiltered = new Map<string, typeof resourcesWithLowercase>();
      filtered.forEach((resources, type) => {
        if (type.toLowerCase().includes(typeSearchLower)) {
          typeFiltered.set(type, resources);
        }
      });
      filtered = typeFiltered;
    }

    if (debouncedNameSearch.trim()) {
      const nameSearchLower = debouncedNameSearch.trim().toLowerCase();
      const nameFiltered = new Map<string, typeof resourcesWithLowercase>();
      filtered.forEach((resources, type) => {
        const matching = resources.filter(r => r.nameLower.includes(nameSearchLower));
        if (matching.length > 0) nameFiltered.set(type, matching);
      });
      filtered = nameFiltered;
    }

    return filtered;
  }, [resourcesByType, debouncedTypeSearch, debouncedNameSearch]);

  return {
    filteredResourcesByType,
    setDebouncedTypeSearch,
    setDebouncedNameSearch,
  };
}

export default useResourceSearch;
